import React, { useState, useRef, useEffect } from "react";
import classNames from "classnames";
import { Avatar } from "./Avatar";
import { Icon } from "@/components/ui/display/Icon";
import type { AvatarGroupProps, AvatarGroupItem } from "./types";
import styles from "./AvatarGroup.module.css";

/**
 * AvatarGroup component - Displays a group of avatars with overflow indicator.
 * Shows a limited number of avatars with "+N" for remaining items.
 * Click to expand vertically showing all users with their names.
 * The first avatar acts as an anchor and stays in place during expansion.
 */
export const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
  (
    {
      items,
      max = 3,
      size = "md",
      overlap = 8,
      expandable = true,
      panelTitle,
      onAvatarClick,
      onOverflowClick,
      className,
      "data-testid": testId,
    },
    ref,
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Ensure max is at least 1
    const effectiveMax = Math.max(1, max);

    // Split items into visible and hidden
    const visibleItems = items.slice(0, effectiveMax);
    const hiddenItems = items.slice(effectiveMax);
    const overflowCount = hiddenItems.length;

    // First item is the anchor
    const anchorItem = items[0];
    const remainingItems = items.slice(1);

    // Close when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsExpanded(false);
        }
      };

      if (isExpanded) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isExpanded]);

    // Close on escape key
    useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setIsExpanded(false);
        }
      };

      if (isExpanded) {
        document.addEventListener("keydown", handleEscape);
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }, [isExpanded]);

    const handleGroupClick = () => {
      if (expandable) {
        setIsExpanded(!isExpanded);
      }
      if (onOverflowClick && overflowCount > 0) {
        onOverflowClick(hiddenItems);
      }
    };

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded(false);
    };

    const handleItemClick = (item: AvatarGroupItem) => {
      if (onAvatarClick) {
        onAvatarClick(item);
        setIsExpanded(false);
      }
    };

    // Always render collapsed group to maintain layout, overlay expanded panel on top
    return (
      <div
        ref={containerRef}
        className={classNames(styles.container, className)}
      >
        <div
          ref={ref}
          className={classNames(styles.group, {
            [styles.clickable]: expandable,
          })}
          style={{ "--avatar-overlap": `-${overlap}px` } as React.CSSProperties}
          onClick={handleGroupClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleGroupClick();
            }
          }}
          role={expandable ? "button" : undefined}
          tabIndex={expandable ? 0 : undefined}
          aria-expanded={expandable ? isExpanded : undefined}
          data-testid={testId}
        >
          {visibleItems.map((item, index) => (
            <div
              key={item.id}
              className={styles.avatarWrapper}
              style={{
                marginLeft: index === 0 ? 0 : `var(--avatar-overlap)`,
                zIndex: index + 1,
              }}
            >
              <Avatar
                src={item.src}
                name={item.name}
                alt={item.alt}
                size={size}
                data-testid={testId ? `${testId}-avatar-${item.id}` : undefined}
              />
            </div>
          ))}

          {overflowCount > 0 && (
            <div
              className={styles.avatarWrapper}
              style={{
                marginLeft: "var(--avatar-overlap)",
                zIndex: visibleItems.length + 1,
              }}
            >
              <Avatar
                name={`+${overflowCount}`}
                size={size}
                className={styles.overflow}
                data-testid={testId ? `${testId}-overflow` : undefined}
              />
            </div>
          )}
        </div>

        {/* Expanded panel overlay - floats above without affecting layout */}
        {isExpanded && expandable && anchorItem && (
          <div className={styles.expandedContainer}>
            {/* Header row: optional title + close button */}
            <div className={styles.headerRow}>
              {panelTitle && (
                <span className={styles.panelTitle}>{panelTitle}</span>
              )}
              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close"
              >
                <Icon name="close" />
              </button>
            </div>

            {/* Anchor row: first avatar + name */}
            <div
              className={styles.anchorRow}
              onClick={() => handleItemClick(anchorItem)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleItemClick(anchorItem);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={styles.anchorAvatar}>
                <Avatar
                  src={anchorItem.src}
                  name={anchorItem.name}
                  alt={anchorItem.alt}
                  size={size}
                />
              </div>
              <span className={styles.anchorName}>{anchorItem.name}</span>
            </div>

            {/* Remaining items */}
            {remainingItems.length > 0 && (
              <div className={styles.expandedList}>
                {remainingItems.map((item) => (
                  <div
                    key={item.id}
                    className={styles.expandedItem}
                    onClick={() => handleItemClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleItemClick(item);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.expandedItemAvatar}>
                      <Avatar
                        src={item.src}
                        name={item.name}
                        alt={item.alt}
                        size={size}
                      />
                    </div>
                    <span className={styles.expandedItemName}>{item.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

AvatarGroup.displayName = "AvatarGroup";

export default AvatarGroup;
